// 9fbef606107a605d69c0edbcd8029e5d
import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import Image from '../../../atoms/Image';
import { getIconPath, isClient } from '../../../../../utils';
import styles from '../styles/CollapsibleContainer.style';
import withStyles from '../../../hoc/withStyles';
import {
  DEFAULT_TRANSITION_FUNCTION,
  DISPLAY_BLOCK,
  FADE_OUT_OPACITY,
  FULL_OPACITY,
  HEIGHT_AUTO,
  OVERFLOW_Y_HIDDEN,
  TRANSITION_PROPERTY,
  UNSET,
} from '../../AccordionCard/hooks/useAccordionTransition.constants';
import {
  cleanupAnimation,
  getFinalHeightForTransition,
  setStylesToRef,
} from '../../AccordionCard/hooks/useAccordionTransition.utils';

import PlacePerkBanner from '../../../../features/CnC/PlacePerkBanner';
import { PAGE_VARIANTS } from '../../../../features/CnC/PlacePerkBanner/PlacePerkConstants';

class CollapsibleContainer extends React.Component {
  static propTypes = {
    header: PropTypes.node.isRequired,
    body: PropTypes.node.isRequired,
    iconOpen: PropTypes.string,
    iconClose: PropTypes.string,
    className: PropTypes.string,
    defaultOpen: PropTypes.bool,
    onToggleCallback: PropTypes.func,
    spc: PropTypes.bool,
    containerId: PropTypes.string,
    isCollapseDisabled: PropTypes.bool,
    transitionConfig: PropTypes.shape({}),
    isUserLoggedIn: PropTypes.bool,
    isPlacePerkEnabled: PropTypes.bool,
  };

  static defaultProps = {
    iconOpen: 'up_arrow_icon',
    iconClose: 'down_arrow_icon',
    className: '',
    defaultOpen: false,
    onToggleCallback: null,
    spc: true,
    containerId: '',
    isCollapseDisabled: false,
    transitionConfig: {},
    isUserLoggedIn: false,
    isPlacePerkEnabled: false,
  };

  constructor(props) {
    super(props);
    const { spc, transitionConfig = {}, transitionConfig: { shouldUseTransition } = {} } = props;
    this.state = {
      isExpanded: props.defaultOpen || false,
      shouldUseTransition: isClient() && shouldUseTransition,
    };
    this.containerRef = createRef();
    this.timeoutId = undefined;
    this.initialAnimationFrameId = undefined;
    this.finalAnimationFrameId = undefined;
    this.expandedHeight = spc && getFinalHeightForTransition(true, transitionConfig);
  }

  componentDidUpdate(prevProps) {
    const { defaultOpen } = this.props;
    if (prevProps.defaultOpen !== defaultOpen) {
      this.updateState();
    }
  }

  componentWillUnmount() {
    cleanupAnimation(this.timeoutId, this.initialAnimationFrameId, this.finalAnimationFrameId);
  }

  updateState = () => {
    const { defaultOpen } = this.props;
    this.setState({
      isExpanded: defaultOpen,
    });
  };

  /**
   * Resets transition styles when transition is done
   */
  resetTransitionStyles = (couponSectionNewDesign) => {
    setStylesToRef(this.containerRef, {
      display: '',
      height: HEIGHT_AUTO,
      overflowY: !couponSectionNewDesign ? OVERFLOW_Y_HIDDEN : '',
      opacity: FULL_OPACITY,
      transition: UNSET,
    });
  };

  /**
   * Set transition styles for final animation frame
   */
  setFinalTransitionStyles = () => {
    const { isExpanded } = this.state;
    const { transitionConfig, transitionConfig: { transitionDuration } = {} } = this.props;
    const collapsedHeight = getFinalHeightForTransition(false, transitionConfig);
    const targetHeight = isExpanded ? collapsedHeight : this.expandedHeight;
    setStylesToRef(this.containerRef, {
      display: DISPLAY_BLOCK,
      height: `${targetHeight}px`,
      overflowY: OVERFLOW_Y_HIDDEN,
      opacity: FADE_OUT_OPACITY,
      transition: `${TRANSITION_PROPERTY} ${transitionDuration} ${DEFAULT_TRANSITION_FUNCTION}`,
    });
  };

  /**
   * Initialize transition styles in first animation frame (set height to measured current height)
   */
  setInitialTransitionStyles = () => {
    const { transitionConfig: { transitionDuration } = {} } = this.props;
    setStylesToRef(this.containerRef, {
      display: DISPLAY_BLOCK,
      height: `${this.containerRef?.current?.scrollHeight}px`,
      overflowY: OVERFLOW_Y_HIDDEN,
      opacity: FULL_OPACITY,
      transition: `${TRANSITION_PROPERTY} ${transitionDuration} ${DEFAULT_TRANSITION_FUNCTION}`,
    });
    // Start final animation frame
    this.finalAnimationFrameId = requestAnimationFrame(this.setFinalTransitionStyles);
  };

  /**
   * Handle collapse/expand with transition
   */
  toggleCollapseStateWithTransition = () => {
    const { transitionConfig: { transitionDurationMs } = {} } = this.props;
    this.initialAnimationFrameId = requestAnimationFrame(this.setInitialTransitionStyles);
    this.timeoutId = setTimeout(() => this.toggleCollapseState(true), transitionDurationMs);
  };

  toggleCollapseState = (resetAnimation = false) => {
    const { onToggleCallback, couponSectionNewDesign } = this.props;
    const { isExpanded } = this.state;
    this.setState({ isExpanded: !isExpanded }, () => {
      if (onToggleCallback) {
        onToggleCallback(this.state);
      }
      if (this.containerRef?.current && !isExpanded) {
        // When expanding, save final height as expanded height
        this.expandedHeight = this.containerRef?.current?.scrollHeight;
      }
      if (resetAnimation) {
        // Reset transition styles when transition ends
        this.resetTransitionStyles(couponSectionNewDesign);
        clearTimeout(this.timeoutId);
      }
    });
  };

  handleOnClick = () => {
    const { shouldUseTransition } = this.state;
    const { isCollapseDisabled, toggleExpandedState, isStoreDetailsRedesignMobileEnabled } =
      this.props;
    if (!isCollapseDisabled) {
      if (shouldUseTransition) {
        this.toggleCollapseStateWithTransition();
      } else {
        this.toggleCollapseState();
        if (isStoreDetailsRedesignMobileEnabled) {
          toggleExpandedState();
        }
      }
    }
  };

  renderArrow = (mobFlag, isExpanded, IconOpen, IconClose) => {
    return (
      mobFlag && (
        <Image
          src={isExpanded ? IconOpen : IconClose}
          alt="" // Empty - VO will read button's aria-expanded state and header text
          aria-hidden
          className="collapsible-icon"
        />
      )
    );
  };

  render() {
    const {
      header,
      subHeader,
      body,
      iconOpen,
      iconClose,
      className,
      spc,
      containerId,
      isCollapseDisabled,
      isStoreDetailsRedesignMobileEnabled,
      isUserLoggedIn,
      isPlacePerkEnabled,
    } = this.props;
    const IconOpen = (iconOpen && getIconPath(iconOpen)) || getIconPath('up_arrow_icon');
    const IconClose = (iconClose && getIconPath(iconClose)) || getIconPath('down_arrow_icon');
    const { isExpanded } = this.state;
    const pageVariant = PAGE_VARIANTS.VIEW_BAG_PAGE;
    return (
      <div className={className}>
        <button
          type="button"
          aria-expanded={!!isExpanded}
          onMouseDown={this.handleOnClick}
          className="collapsible-header"
          aria-controls={containerId}
          aria-hidden={isCollapseDisabled ? 'true' : 'false'}
          tabIndex={isCollapseDisabled ? -1 : 0}
        >
          {spc === true ? (
            <div className={isExpanded ? 'expanded-header' : 'collapsed-header'}>
              {header}

              {!isExpanded && subHeader}
            </div>
          ) : (
            <Image
              src={isExpanded ? IconOpen : IconClose}
              alt="" // Empty - VO will read button's aria-expanded state and header text
              aria-hidden
              className="collapsible-icon"
            />
          )}
          {this.renderArrow(isStoreDetailsRedesignMobileEnabled, isExpanded, IconOpen, IconClose)}
        </button>

        <div
          id={containerId}
          ref={this.containerRef}
          className={isExpanded ? 'item-opened' : 'item-closed'}
        >
          {body}
          {spc === false && isUserLoggedIn && isPlacePerkEnabled && (
            <PlacePerkBanner pageVariant={pageVariant} />
          )}
        </div>
      </div>
    );
  }
}

export default withStyles(CollapsibleContainer, styles);
export { CollapsibleContainer as CollapsibleContainerVanilla };
